import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select'
import { NgxMaskDirective } from 'ngx-mask'
import { IPlanDetail } from '@appShared/interfaces/[Model-based]/plan-detail.interface'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { take } from 'rxjs/operators'
import { ButtonComponent } from '@appShared/components/button/button.component'
import { AdminService, ShareholderEditSectionData, ShareholderEditSection_ } from '@appShared/services/admin.service'
import { AppFacade } from '@appShared/services/app.facade'
import { CommonService } from '@appShared/services/common.service'
import { IDomainModel } from '@appShared/services/lookup/[CodeGen]/domain-model.interface'
import { PlanType } from '@appShared/services/lookup/[CodeGen]/plan-type.domain'

@Component({
   selector: 'app-shareholder-plan-detail-edit',
   templateUrl: './shareholder-plan-detail-edit.component.html',
   // style hack for the dropdown to go outside modal: https://stackoverflow.com/questions/61011313/ng-select-dropdown-is-hidden-by-the-dialog
   styles: [`
      ::ng-deep .ng-dropdown-panel {z-index: 9999999!important;}
   `],
   imports: [
      ReactiveFormsModule,
      NgSelectComponent,
      NgOptionComponent,
      NgxMaskDirective,
      ButtonComponent
   ]
})
export class ShareholderPlanDetailEditComponent implements OnInit {
   @Input() planDetail: IPlanDetail
   @Input() canEdit: boolean
   @Output() shareholderUpdated = new EventEmitter();

   selectedPlanDetail: IPlanDetail
   isLoading: boolean

   /* form/form-fields */
   planDetailForm: FormGroup
   private _stockPlanBeginingYear: number
   private _currentYear = new Date().getFullYear();
   yearDropdown: FormControl
   years: IDomainModel[] = [];
   adjustedShareOffer: FormControl
   purchasedShares: FormControl
   repurchasedShares: FormControl
   repurchasedAmount: FormControl
   evergreenShares: FormControl
   evergreenVestedShares: FormControl
   mouseoverPlanDetailSubmit: boolean
   isSubmitting: boolean

   constructor(
      private _appFacade: AppFacade,
      private _activeModal: NgbActiveModal,
      private _commonService: CommonService,
      private _adminService: AdminService
   ) {
   }

   ngOnInit(): void {
      this.isLoading = true

      this._appFacade.siteConfig$.pipe(take(1)).subscribe(siteConfig => {
         this._stockPlanBeginingYear = siteConfig.stockPlanBeginingYear
         this._createFormGroup()
      })
   }

   /*
    * events
    * */

   onYearChange(year: IDomainModel): void {
      const plan = this.planDetail.plan

      let yearCode = year.code
      this._adminService
         .getPlanDetailByYear(plan.shareholderId, plan.id, yearCode)
         .then(existingPlanDetail => {
            if (existingPlanDetail) {
               this.planDetail = existingPlanDetail
               this._setPlanDetailData(existingPlanDetail)
            }
            else {
               this._setPlanDetailData(({ id: 0, planId: plan.id, plan: { ...plan }, year: yearCode } as IPlanDetail))
            }
         })
         .catch(() => {
            /* Do Nothing */
         })
         .finally(() => (this.isLoading = false))
   }

   /*
   * private methods
   * */

   private _createFormGroup(): void {
      let firstYear
      let lastYear

      const plan = this.planDetail.plan
      const planTypeCode = plan?.typeCode
      if (planTypeCode) {
         const planType = PlanType.toDomainModel(planTypeCode)
         firstYear = plan.year
         lastYear = firstYear + planType.yearSpan
         lastYear = lastYear < this._currentYear
            ? this._currentYear
            : lastYear
      }
      else {
         firstYear = this._stockPlanBeginingYear
         lastYear = this._currentYear
      }

      for (let i = firstYear; i <= lastYear; i++) {
         if (!this.planDetail.year) {
            const existingPlanDetail = plan.planDetails.find(plnDtl => plnDtl.year === i)
            if (!existingPlanDetail) {
               this.planDetail.year = i
            }
         }

         this.years.push({ code: i, description: i.toString() } as IDomainModel)
      }
      const year = this.years.find(yr => yr.code === this.planDetail.year) ?? this.years[this.years.length - 1]

      this.yearDropdown = new FormControl(null, Validators.required)
      this.adjustedShareOffer = new FormControl(null, Validators.required)
      this.purchasedShares = new FormControl(null, Validators.required)
      this.repurchasedShares = new FormControl(null, Validators.required)
      this.repurchasedAmount = new FormControl(null, Validators.required)
      this.evergreenShares = new FormControl(null, Validators.required)
      this.evergreenVestedShares = new FormControl(null, Validators.required)

      this.planDetailForm = new FormGroup({
         yearDropdown: this.yearDropdown,
         adjustedShareOffer: this.adjustedShareOffer,
         purchasedShares: this.purchasedShares,
         repurchasedShares: this.repurchasedShares,
         repurchasedAmount: this.repurchasedAmount,
         evergreenShares: this.evergreenShares,
         evergreenVestedShares: this.evergreenVestedShares
      })

      this.onYearChange(year)
   }

   private _setPlanDetailData(selectedPlanDetail: IPlanDetail): void {
      const setNumber = this._commonService.setNumber
      let year

      if (selectedPlanDetail.year) {
         year = this.years.find(yr => selectedPlanDetail.year === yr.code)
      }

      this.planDetailForm.patchValue({
         yearDropdown: year || this.years[this.years.length - 1],
         adjustedShareOffer: setNumber(selectedPlanDetail.adjustedShareOffer),
         purchasedShares: setNumber(selectedPlanDetail.purchasedShares),
         repurchasedShares: setNumber(selectedPlanDetail.repurchasedShares),
         repurchasedAmount: setNumber(selectedPlanDetail.repurchasedAmount),
         evergreenShares: setNumber(selectedPlanDetail.evergreenShares),
         evergreenVestedShares: setNumber(selectedPlanDetail.evergreenVestedShares)
      })

      this.selectedPlanDetail = selectedPlanDetail

      selectedPlanDetail.id ? this.yearDropdown.disable() : this.yearDropdown.enable()

      this.canEdit ? this.planDetailForm.enable() : this.planDetailForm.disable()
   }

   /*
    * public methods
    * */

   savePlanDetail(): void {
      this.isSubmitting = true

      const planDetailFormValues = this.planDetailForm.value
      const updatedPlanDetail = {
         ...this.selectedPlanDetail,
         year: planDetailFormValues.yearDropdown?.code,
         adjustedShareOffer: planDetailFormValues.adjustedShareOffer,
         purchasedShares: planDetailFormValues.purchasedShares,
         repurchasedShares: planDetailFormValues.repurchasedShares,
         repurchasedAmount: planDetailFormValues.repurchasedAmount,
         evergreenShares: planDetailFormValues.evergreenShares,
         evergreenVestedShares: planDetailFormValues.evergreenVestedShares
      } as IPlanDetail

      this._adminService
         .createUpdatePlanDetail(updatedPlanDetail)
         .then(shareholder => {
            const msgSuffix = updatedPlanDetail.id ? 'updated' : 'created'
            this._commonService.messageUser(`Plan Detail ${msgSuffix}!`)

            this.shareholderUpdated.emit({
               shareholderEditSection: ShareholderEditSection_.planInfo,
               shareholder,
               refresh: true
            } as ShareholderEditSectionData)

            this._activeModal.close()
         })
         .catch(() => {
            /* Do Nothing */
         })
         .finally(() => setTimeout(() => (this.isSubmitting = false), 500))
   }
}
